import React, { useState } from "react";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
    DragStartEvent,
} from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FormattedMessage } from "react-intl";
import IOrder_ItemUI from "../../../models/IOrder_ItemUI";
import IOwTableCols from "../../../models/IOwTableCols";
import IUserUI from "../../../models/IUserUI";
import ISupplierModel from "../../../models/ISupplierModel";
import TableTypeEnum from "../../../models/TableTypeEnum";
import OwLabel from "../../shared/OwLabel/OwLabel";
import * as _ from "lodash-es";
import "./DraggableSortableTable.scss";

type Props = {
    orderItems: IOrder_ItemUI[];
    columns: IOwTableCols[];
    tableType: TableTypeEnum;
    isDataFetching: boolean;
    isInitialData: boolean;
    sortCol: string;
    userModel: IUserUI;
    supplierModel: ISupplierModel;
    onDragStart: (event: DragStartEvent) => void;
    onDragEnd: (event: DragEndEvent) => void;
};

type DraggableRowProps = {
    item: IOrder_ItemUI;
    index: number;
    columns: IOwTableCols[];
    hasChildren: boolean;
    isExpanded: boolean;
    onToggleChildren: () => void;
};

type DraggableChildRowProps = {
    item: IOrder_ItemUI;
    index: number;
    columns: IOwTableCols[];
};

// Render cell value based on column configuration
const renderCellValue = (col: IOwTableCols, row: IOrder_ItemUI, rowIndex: number) => {
    if (col.renderFn) {
        return col.renderFn(row, rowIndex);
    }

    let value: any = row;

    // Navigate to the value using valueProp (supports nested like "Catalog_Item.name")
    if (col.valueProp) {
        const props = col.valueProp.split(".");
        for (const prop of props) {
            value = value?.[prop];
        }
    }

    if (col.valueSubProp && value) {
        value = value[col.valueSubProp];
    }

    // Handle arrays
    if (col.isArray && Array.isArray(value) && col.arrayIndex !== undefined) {
        value = value[col.arrayIndex];
    }

    return value ?? "";
};

const DraggableRow: React.FC<DraggableRowProps> = ({ item, index, columns, hasChildren, isExpanded, onToggleChildren }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.guid });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <tr ref={setNodeRef} style={style} className={isDragging ? "is-dragging" : ""}>
            {/* Drag handle column with optional child toggle */}
            <td className="drag-handle-cell" style={{ width: "50px" }}>
                <div className="d-flex align-items-center">
                    <div {...attributes} {...listeners} className="p-2" style={{ cursor: "grab" }}>
                        <i className="fas fa-grip-vertical text-muted" />
                    </div>
                    {hasChildren && (
                        <button onClick={onToggleChildren} className="btn btn-sm btn-link p-0 ms-1" title="Toggle child items">
                            <i className={`fas fa-chevron-${isExpanded ? "up" : "down"} text-primary`} />
                        </button>
                    )}
                </div>
            </td>

            {/* Render all other columns */}
            {columns.map((col, colIndex) => {
                if (col.notVisible) {
                    return null;
                }

                if (!_.isNil(col.isChecked) && !col.isChecked()) {
                    return null;
                }

                return (
                    <td key={colIndex} className={col.className}>
                        {renderCellValue(col, item, index)}
                    </td>
                );
            })}
        </tr>
    );
};

const DraggableChildRow: React.FC<DraggableChildRowProps> = ({ item, index, columns }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.guid });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <tr ref={setNodeRef} style={style} className={`child-item-row ${isDragging ? "is-dragging" : ""}`}>
            {/* Drag handle column for child */}
            <td className="drag-handle-cell child-drag-handle" style={{ width: "50px" }}>
                <div className="d-flex align-items-center ps-3">
                    <i className="fas fa-level-up-alt fa-rotate-90 text-muted me-2" style={{ fontSize: "0.8rem" }} />
                    <div {...attributes} {...listeners} className="p-2" style={{ cursor: "grab" }}>
                        <i className="fas fa-grip-vertical text-muted" />
                    </div>
                </div>
            </td>

            {/* Render all other columns */}
            {columns.map((col, colIndex) => {
                if (col.notVisible) {
                    return null;
                }

                if (!_.isNil(col.isChecked) && !col.isChecked()) {
                    return null;
                }

                return (
                    <td key={colIndex} className={col.className}>
                        {renderCellValue(col, item, index)}
                    </td>
                );
            })}
        </tr>
    );
};

/**
 * Draggable/Sortable table for order items with parent/child relationships
 *
 * IMPORTANT: Understanding the Order Items Structure
 *
 * Backend returns Order_Items array containing:
 * 1. Parent items (no parent_order_item_id)
 * 2. Regular child items (is_child_item = true, has parent_order_item_id)
 *
 * Kit items are handled differently:
 * - Kit items (is_child_item = false, has parent_order_item_id) are REMOVED from Order_Items array
 * - They are nested in parent.Kit_Items array and shown via KitItemsDisplay component
 * - Kit items are NOT sortable/draggable - they're read-only display items
 *
 * Sort Order Rules:
 * - Parent items: sequential sort_order (1, 2, 3...)
 * - Kit items: sequential sort_order within parent (1, 2, 3...) - stored in Kit_Items array
 * - Child items: sort_order continues after kit items (e.g., if 2 kit items exist, child starts at 3, 4, 5...)
 *
 * Example structure:
 * Parent 1 (sort_order: 1)
 *   ├─ Kit Item 1 (sort_order: 1, in Kit_Items array) [NOT SORTABLE]
 *   ├─ Kit Item 2 (sort_order: 2, in Kit_Items array) [NOT SORTABLE]
 *   ├─ Child Item 1 (sort_order: 3, item_no: "1.1", in Order_Items array) [SORTABLE]
 *   └─ Child Item 2 (sort_order: 4, item_no: "1.2", in Order_Items array) [SORTABLE]
 * Parent 2 (sort_order: 2)
 */
export const DraggableSortableTable: React.FC<Props> = (props) => {
    const [expandedParentIds, setExpandedParentIds] = useState<Set<string>>(new Set());

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Helper functions
    const getParentItems = () => {
        return props.orderItems.filter((item) => !item.parent_order_item_id);
    };

    const getChildrenOf = (parentId: number) => {
        // Returns only regular child items (is_child_item = true)
        // Kit items are in parent.Kit_Items array and not sortable
        return props.orderItems
            .filter((item) => item.parent_order_item_id === parentId)
            .sort((a, b) => {
                // Child items sort by sort_order which includes offset for kit items
                // sort_order should always be defined for child items, but fallback to item_no if needed
                const sortA = (a.sort_order ?? Number(a.item_no)) || 0;
                const sortB = (b.sort_order ?? Number(b.item_no)) || 0;
                return sortA - sortB;
            });
    };

    const hasChildren = (parentId: number) => {
        return props.orderItems.some((item) => item.parent_order_item_id === parentId);
    };

    const toggleChildExpansion = (parentGuid: string) => {
        setExpandedParentIds((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(parentGuid)) {
                newSet.delete(parentGuid);
            } else {
                newSet.add(parentGuid);
            }
            return newSet;
        });
    };

    const parentItems = getParentItems();
    const parentIds = parentItems.map((item) => item.guid);

    // Render table headers
    const renderHeaders = () => {
        return (
            <>
                {/* Drag handle header */}
                <th style={{ width: "50px" }} className="align-middle">
                    <i className="fas fa-grip-vertical text-muted" />
                </th>

                {/* Render all column headers */}
                {props.columns.map((col, colIndex) => {
                    if (col.notVisible) {
                        return null;
                    }

                    if (!_.isNil(col.isChecked) && !col.isChecked()) {
                        return null;
                    }

                    const title = col.skipTitleTranslate ? (
                        col.title
                    ) : (
                        <OwLabel
                            supplierModel={props.supplierModel}
                            isSupplierVendorOverride={col.isVendorOverride}
                            noBottomMargin={true}
                            title={col.title}
                        />
                    );

                    return (
                        <th key={colIndex} className={`${col.className || ""} align-middle`}>
                            {title}
                        </th>
                    );
                })}
            </>
        );
    };

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={props.onDragStart} onDragEnd={props.onDragEnd}>
            <div className="alert alert-info mb-3">
                <i className="fas fa-info-circle me-2" />
                <FormattedMessage id="drag_mode_active" defaultMessage="Drag mode is active. Use the grip handles to reorder items." />
            </div>
            <SortableContext items={parentIds} strategy={verticalListSortingStrategy}>
                <table
                    className={`table ${props.tableType === TableTypeEnum.SMALL ? "table-sm" : ""} table-hover draggable-sortable-table`}
                    id="orderItemTable">
                    <thead>
                        <tr>{renderHeaders()}</tr>
                    </thead>
                    <tbody>
                        {parentItems.map((item, index) => {
                            const childItems = getChildrenOf(item.id!);
                            const hasChildItems = hasChildren(item.id!);
                            const isExpanded = expandedParentIds.has(item.guid);

                            return (
                                <React.Fragment key={item.guid}>
                                    <DraggableRow
                                        item={item}
                                        index={index}
                                        columns={props.columns}
                                        hasChildren={hasChildItems}
                                        isExpanded={isExpanded}
                                        onToggleChildren={() => toggleChildExpansion(item.guid)}
                                    />

                                    {/* Render child items if expanded */}
                                    {isExpanded && childItems.length > 0 && (
                                        <SortableContext items={childItems.map((c) => c.guid)} strategy={verticalListSortingStrategy}>
                                            {childItems.map((childItem, childIndex) => (
                                                <DraggableChildRow
                                                    key={childItem.guid}
                                                    item={childItem}
                                                    index={childIndex}
                                                    columns={props.columns}
                                                />
                                            ))}
                                        </SortableContext>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </SortableContext>
        </DndContext>
    );
};
